/**
 * STEP-UP → HEBREW. `apps/api/src/services/authentication.ts`, 04 §2.2 / §2.3.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * "A RULE WITH NO PATH TO COMPLIANCE IS NOT A RULE."
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * That sentence is `routes/session.ts`'s, about the gap it closed: the platform MFA floor was binding
 * and unconditional, the service methods existed, and there was **no route to enrol through**. A
 * platform principal signing in for the first time was answered `MFA_ENROLMENT_REQUIRED` and had
 * nowhere to go.
 *
 * The route exists now, and it can answer 403 with `need`. This module is the other half: turning
 * that `need` into the ONE sentence that says which door to open. The route's own comment is the
 * specification —
 *
 *   > *`need` is disclosable: the caller is already authenticated, and telling them WHICH challenge
 *   > to satisfy is the difference between a usable product and a locked door.*
 *
 * ── THE THREE NEEDS ARE THREE DIFFERENT PEOPLE'S NEXT FIVE MINUTES ──────────────────────────────
 *
 *   REAUTH    the session is stale. Sign in again — nothing is wrong and nothing is missing.
 *   MFA       a second factor is enrolled and has not been presented in this session. Open the
 *             authenticator. No enrolment is needed and offering one would be a re-enrolment, which
 *             is how an unattended browser becomes a second enrolled device.
 *   ENROLMENT there is no second factor yet. This is the only one of the three that ends somewhere
 *             other than where it started.
 *
 * Collapsing them into "נדרש אימות נוסף" would send a person with a working authenticator to an
 * enrolment screen, which is the exact action the step-up was protecting against.
 *
 * ── AND THIS IS NOT A DENIAL ────────────────────────────────────────────────────────────────────
 *
 * It arrives as a 403 and it is NOT a `DenyBody`: there is no `reason`, no `stage`, and the gate did
 * not produce it — the handler did, after the gate allowed the request. `<DenialNotice>` is therefore
 * the wrong component for it, and this module exists so that it does not get used for one.
 *
 * Pure. Maps a state the server named. Decides nothing, and never evaluates whether a step-up is due.
 */

import type { StepUpNeed } from './contract';

export interface StepUpText {
  readonly titleHe: string;
  readonly explanationHe: string;
  /** One written next step. Which control it names differs per need — that is the point. */
  readonly nextStepHe: string;
  /**
   * Which address the next step belongs on. `'challenge'` and `'enrolment'` are two DIFFERENT
   * screens, and the screen wires them; this module never builds an address.
   */
  readonly action: 'sign_in_again' | 'challenge' | 'enrolment';
  readonly tone: 'warn' | 'error';
}

const STEP_UP_HE: Readonly<Record<StepUpNeed, StepUpText>> = {
  REAUTH: {
    titleHe: 'נדרשת התחברות מחודשת',
    explanationHe:
      'הפעולה הזו דורשת התחברות עדכנית. ההתחברות הנוכחית ותיקה מדי עבורה, ולכן היא לא בוצעה. ' +
      'שום דבר לא נשמר ולא השתנה.',
    nextStepHe: 'התחברות מחדש, ולאחריה אפשר לבצע את הפעולה שוב.',
    action: 'sign_in_again',
    tone: 'warn',
  },
  MFA: {
    titleHe: 'נדרש אימות דו-שלבי לפעולה הזו',
    explanationHe:
      'רשום עבורך אימות דו-שלבי, והוא טרם הוצג בהתחברות הנוכחית. הפעולה לא בוצעה ולא נשמר שינוי.',
    // Deliberately NOT an enrolment link: the factor already exists, and "enrol again" on this path
    // is how an unattended browser becomes a second enrolled device.
    nextStepHe: 'הזנת הקוד המוצג ביישומון האימות שכבר רשום עבורך.',
    action: 'challenge',
    tone: 'warn',
  },
  ENROLMENT: {
    titleHe: 'נדרש רישום אימות דו-שלבי',
    explanationHe:
      'הפעולה הזו דורשת אימות דו-שלבי, ולחשבון שלך טרם נרשם יישומון אימות. הפעולה לא בוצעה.',
    nextStepHe: 'רישום יישומון אימות ושמירת קודי השחזור, ולאחר מכן אפשר לחזור לפעולה.',
    action: 'enrolment',
    tone: 'error',
  },
};

/** Exposed for the mirror test only. */
export function mappedStepUpNeeds(): readonly string[] {
  return Object.keys(STEP_UP_HE);
}

export function stepUpTextFor(need: StepUpNeed): StepUpText {
  return STEP_UP_HE[need];
}

/**
 * Reads `need` off a 403 body that is NOT a denial.
 *
 * `null` when the field is absent or is a value this build does not know — and `null` means the
 * screen falls back to the ordinary refusal wording rather than guessing a challenge. A guess here
 * would send somebody to the wrong screen, which is worse than saying less.
 */
export function stepUpNeedOf(body: unknown): StepUpNeed | null {
  if (body === null || typeof body !== 'object') return null;
  const need = (body as Record<string, unknown>)['need'];
  if (typeof need !== 'string') return null;
  return need in STEP_UP_HE ? (need as StepUpNeed) : null;
}
